// Used in home-page-container.component, teacher-classes.component, teacher-classes.effect, login-form.effect, login-profile.effect

// Angular
import { Injectable } from "@angular/core";

// Usher
import { UsherData, UsherService } from "@carnegie-learning/usher";

// Services/Types/Helpers
import { CookieService, LocalStorage } from "#core/services";
import { Course, PPUser } from "cli-types";

// Store
import { ValueList } from "#core/models/valueList.interface";
import * as coursesStore from "#core/teacher-classes/store";
import { School } from "@carnegie-learning/data-access-fido";
import { isDefined } from "@carnegie-learning/utility-types";
import { Store, select } from "@ngrx/store";

@Injectable()
/**
 * @class UsherPayloadHelper
 * @desc Helper function formatting Passport related data to the Pendo Usher Service
 *
 * Payload example:
 * export interface UsherData {
 * userId: UserId; // UUID
 * schoolId: SchoolId; // FIDO SchoolID
 * districtId?: DistrictId; // FIDO DistrictID
 * role: string;
 * application: string;
 * version: SemVer;
 * [customUserData: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
 * }
 */
export class UsherPayloadHelper {
    USHER_LOCALSTORAGE_KEY = "usherData";

    constructor(
        private usherService: UsherService,
        private localStorage: LocalStorage,
        private store_courses: Store<coursesStore.CoursesState>,
        private cookieService: CookieService
    ) {}

    // starts usher tracking
    initializeUser() {
        const userProfile: PPUser = this.localStorage.get("userProfile");

        // RUSH service now requires CL_TOKEN2, demo doesn't comply until refresh
        // so need to refresh the webpage to make sure Pendo runs
        const clToken2 = this.cookieService.get('CL_TOKEN2');
        if (
            (<any>window).isDemo &&
            this.usherService &&
            !(<any>this.usherService).metadataInitialized &&
            clToken2 &&
            userProfile
        ) {
            this.localStorage.set("demo-reload", 1);
            (<any>window).location = window.location.pathname + '?v=' + new Date().getTime();
            return;
        }

        const schools: School[] = this.localStorage.get("fidoSchoolList");
        const selectedCourse: Course = this.localStorage.get("selectedCourse");

        // TODO: don't have access to FIDO roles; do have access to PP roles
        const role =
            userProfile.roleLevel === 5
                ? "student"
                : userProfile.roleLevel === 4
                ? "instructor"
                : userProfile.roleLevel === 1
                ? "distributor"
                : "other";

        const schoolIds: string[] = schools.map((school) => <string>school.schoolid);
        const districtIds = Array.from(
            new Set(schools.map((school) => school.districtId))
        ).filter(isDefined);

        let data: UsherData = {
            userId: userProfile.userGUID,
            role: role,
            application: "Passport",
            version: (<any>window).buildNumber,
            classId: "",
            schoolId: schools.length === 1 ? <string>schoolIds[0] : "",
            schoolIds,
            districtId: districtIds.length === 1 ? districtIds[0] : "",
            districtIds,
        };

        if (selectedCourse) {
            data = {
                ...data,
                editionId: selectedCourse.editionId || "",
                subjectId: selectedCourse.subjectId || "",
                levelIds: [selectedCourse.level],
            };
        } else {
            data = {
                ...data,
                editionId: "",
                subjectId: "",
                levelIds: [],
            };
        }
        if (clToken2) {
            this.usherService.initialize(data);
        }
    }

    // ends usher tracking
    disable() {
        this.usherService.disable();
    }

    // class will automatically pass the associated license data for targeting of Pendo usher
    setClassData(course: Course) {
        if (course) {
            const schools: School[] = this.localStorage.get("fidoSchoolList");
            const currentSchoolData = schools
                ? schools.find((school) => school.schoolid === course.schoolID)
                : null;

            // classes may not have subjectID or editionID due to the way rostering occurs
            const updatedData: Partial<UsherData> = {
                classId: course.courseGUID ? course.courseGUID : "",
                schoolId: currentSchoolData ? currentSchoolData.schoolid : "",
                districtId: currentSchoolData?.districtId || "",
                editionId: course.editionId || "",
                subjectId: course.subjectId || "",
                levelIds: [course.level],
            };
            this.usherService.update({ ...updatedData });
        }
    }

    setDataForContentLibrary(valueList: ValueList) {
        if (valueList) {
            this.store_courses
            .pipe(select(coursesStore.getAllActiveEditions))
            .subscribe((editions) => {
                const edition = editions.find(data => data.editionId === valueList.id);
                if (edition) {
                    const updatedData: Partial<UsherData> = {
                        editionId: edition.editionId || "",
                        subjectId: edition.subjectId || "",
                        levelIds: edition.levelIds || [],
                    };
                    this.usherService.update(updatedData);
                }
            });
        }
    }
    resetCurrentSchoolDistrict() {
        const updatedData: Partial<UsherData> = {
            classId: "",
            schoolId: "",
            districtId: "",
            editionId: "",
            levelIds: [],
            subjectId: "",
        };
        this.usherService.update({ ...updatedData });
    }
}
